import { authorSchema, deviceRevokedReasonSchema } from './chunk-PLMCPVKY.js';
import { uuidSchema, base64UrlSchema } from './chunk-EWGKAU72.js';
import { z } from 'zod';

var SYNC_PROTOCOL_VERSION = 1;
var SYNC_PATH = "/v1/sync";
var SYNC_PING_INTERVAL_MS = 25e3;
var MAX_UPDATE_BYTES = 2 * 1024 * 1024;
var MAX_SNAPSHOT_BYTES = 16 * 1024 * 1024;
var SNAPSHOT_THRESHOLD_UPDATES = 500;
var SNAPSHOT_THRESHOLD_BYTES = 1024 * 1024;
var seqSchema = z.number().int().positive();
var sinceSchema = z.number().int().nonnegative();
var payloadSchema = base64UrlSchema;
var clientMsgIdSchema = z.string().min(1).max(64);
var deviceIdSchema = z.string().min(1).max(64);
var syncAuthSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("user"), accessToken: z.string().min(1) }),
  z.object({ kind: z.literal("guest"), shareId: uuidSchema, guestToken: z.string().min(1) })
]);
var CLIENT_PLATFORMS = ["ios", "web"];
var helloMessageSchema = z.object({
  type: z.literal("hello"),
  protocol: z.literal(SYNC_PROTOCOL_VERSION),
  auth: syncAuthSchema,
  deviceId: deviceIdSchema,
  client: z.object({ platform: z.enum(CLIENT_PLATFORMS), version: z.string().min(1) })
});
var MAX_SUBSCRIBE_DOCS = 1e3;
var subscribeMessageSchema = z.object({
  type: z.literal("subscribe"),
  docs: z.array(z.object({ docId: uuidSchema, since: sinceSchema })).min(1).max(MAX_SUBSCRIBE_DOCS).refine((docs) => new Set(docs.map((d) => d.docId)).size === docs.length, {
    message: "duplicate docId in subscribe"
  })
});
var unsubscribeMessageSchema = z.object({
  type: z.literal("unsubscribe"),
  docIds: z.array(uuidSchema).min(1)
});
var pushMessageSchema = z.object({
  type: z.literal("push"),
  docId: uuidSchema,
  clientMsgId: clientMsgIdSchema,
  payload: payloadSchema
});
var snapshotMessageSchema = z.object({
  type: z.literal("snapshot"),
  docId: uuidSchema,
  clientMsgId: clientMsgIdSchema,
  /** The snapshot includes every update with seq ≤ baseSeq. */
  baseSeq: sinceSchema,
  payload: payloadSchema
});
var clientAwarenessMessageSchema = z.object({
  type: z.literal("awareness"),
  docId: uuidSchema,
  payload: payloadSchema
});
var clientMessageSchema = z.discriminatedUnion("type", [
  helloMessageSchema,
  subscribeMessageSchema,
  unsubscribeMessageSchema,
  pushMessageSchema,
  snapshotMessageSchema,
  clientAwarenessMessageSchema
]);
var welcomeMessageSchema = z.object({
  type: z.literal("welcome"),
  /** null for guests. */
  userId: uuidSchema.nullable(),
  serverTime: z.string(),
  protocol: z.literal(SYNC_PROTOCOL_VERSION)
});
var authorFieldSchema = authorSchema.optional();
var updateItemSchema = z.object({
  seq: seqSchema,
  payload: payloadSchema,
  deviceId: deviceIdSchema,
  author: authorFieldSchema
});
var updatesMessageSchema = z.object({
  type: z.literal("updates"),
  docId: uuidSchema,
  snapshot: z.object({ seq: sinceSchema, payload: payloadSchema }).nullable(),
  items: z.array(updateItemSchema),
  /** The last part of a catch-up response has `upToDate: true`. */
  upToDate: z.boolean()
});
var updateMessageSchema = z.object({
  type: z.literal("update"),
  docId: uuidSchema,
  seq: seqSchema,
  payload: payloadSchema,
  deviceId: deviceIdSchema,
  author: authorFieldSchema
});
var ackMessageSchema = z.object({
  type: z.literal("ack"),
  docId: uuidSchema,
  clientMsgId: clientMsgIdSchema,
  /** seq of the stored update; for a snapshot its baseSeq (may be 0). */
  seq: sinceSchema
});
var serverAwarenessMessageSchema = z.object({
  type: z.literal("awareness"),
  docId: uuidSchema,
  payload: payloadSchema,
  deviceId: deviceIdSchema
});
var SYNC_ERROR_CODES = [
  "bad_request",
  "unauthorized",
  "forbidden",
  "doc_not_found",
  "payload_too_large",
  "rate_limited",
  "protocol_mismatch",
  "internal",
  "device_revoked"
];
var errorMessageSchema = z.object({
  type: z.literal("error"),
  code: z.enum(SYNC_ERROR_CODES),
  message: z.string(),
  /** clientMsgId or docId the error refers to. */
  ref: z.string().optional(),
  /** For `rate_limited`: how long to wait before reconnecting/resending. */
  retryAfterMs: z.number().int().nonnegative().optional(),
  /** Present with `device_revoked` (D-53). */
  reason: deviceRevokedReasonSchema.optional()
});
var SYNC_CLOSE_DEVICE_REVOKED = 4410;
var serverMessageSchema = z.discriminatedUnion("type", [
  welcomeMessageSchema,
  updatesMessageSchema,
  updateMessageSchema,
  ackMessageSchema,
  serverAwarenessMessageSchema,
  errorMessageSchema
]);
function parseServerMessage(raw) {
  return parseFrame(raw, serverMessageSchema);
}
function parseClientMessage(raw) {
  return parseFrame(raw, clientMessageSchema);
}
function parseFrame(raw, schema) {
  let json;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const result = schema.safeParse(json);
  return result.success ? result.data : null;
}

export { CLIENT_PLATFORMS, MAX_SNAPSHOT_BYTES, MAX_SUBSCRIBE_DOCS, MAX_UPDATE_BYTES, SNAPSHOT_THRESHOLD_BYTES, SNAPSHOT_THRESHOLD_UPDATES, SYNC_CLOSE_DEVICE_REVOKED, SYNC_ERROR_CODES, SYNC_PATH, SYNC_PING_INTERVAL_MS, SYNC_PROTOCOL_VERSION, ackMessageSchema, clientAwarenessMessageSchema, clientMessageSchema, errorMessageSchema, helloMessageSchema, parseClientMessage, parseServerMessage, pushMessageSchema, serverAwarenessMessageSchema, serverMessageSchema, snapshotMessageSchema, subscribeMessageSchema, syncAuthSchema, unsubscribeMessageSchema, updateItemSchema, updateMessageSchema, updatesMessageSchema, welcomeMessageSchema };
//# sourceMappingURL=chunk-WZE4WKKF.js.map
//# sourceMappingURL=chunk-WZE4WKKF.js.map